import type { ArdoqError } from '@ardoq/common-helpers';
import { PreviewContent } from '@ardoq/api-types';
import { Locale } from '@ardoq/locale';

export type ButtonPosition = 'left' | 'right';

export type ContentControls = {
  zoomIn?: boolean;
  zoomOut?: boolean;
  reset?: boolean;
  center?: boolean;
};

type ImagePreviewLink = {
  label: string;
  href: string;
};

export type ImagePreviewLabels = {
  zoomToLabel?: string;
  zoomInLabel?: string;
  zoomOutLabel?: string;
  resetZoomLabel?: string;
  clearBackgroundTooltip?: string;
  darkBackgroundTooltip?: string;
};

export type ImagePreviewBaseProps = {
  maxScale?: number;
  minScale?: number;
  zoomStep?: number;
  controls?: ContentControls;
  url: string;
  link?: ImagePreviewLink;
  height?: string;
  contentControlsPosition?: ButtonPosition;
  labels?: ImagePreviewLabels;
};

export type ImagePreviewProps = ImagePreviewBaseProps & {
  limitToBounds?: boolean;
  commands: {
    onChangeBackgroundColor: (color: string) => void;
    onFetchImage: (url: string) => void;
  };
};

export type NodeOption = {
  id: string;
  label: string;
  shapeId: string;
  type?: string;
};
export type ZoomInOptions = NodeOption[];

export type ContentControlsProps = {
  backgroundColor: string;
  maxScale: number;
  minScale: number;
  zoomStep: number;
  link?: ImagePreviewLink;
  zoomInOptions: ZoomInOptions;
  onChangeBackgroundColor: (color: string) => void;
  buttonsPosition?: ButtonPosition;
  labels?: ImagePreviewLabels;
};

export enum PreviewStateStatus {
  IDLE = 'idle',
  Loading = 'loading',
  Error = 'error',
  Loaded = 'loaded',
}
export type ErrorContent = {
  title: string;
  error: ArdoqError;
};

type BasePreviewState = {
  backgroundColor: string;
  locale: Locale;
  zoomInOptions: NodeOption[];
};

export type PreviewContentError = BasePreviewState & {
  status: PreviewStateStatus.Error;
  errorContent: ErrorContent;
};

export type PreviewContentSuccess = BasePreviewState & {
  status: PreviewStateStatus.Loaded;
  content: PreviewContent;
};

type PreviewStates =
  | (BasePreviewState & {
      status: PreviewStateStatus.Loading | PreviewStateStatus.IDLE;
    })
  | PreviewContentError
  | PreviewContentSuccess;

export type ImagePreviewState = PreviewStates & {
  backgroundColor: string;
  locale: Locale;
  zoomInOptions: NodeOption[];
};

export type ImagePreviewErrorMessages = {
  errorTitle?: string;
  fetchFailedMessage?: string;
};

export type ShowImagePreviewModalProps = ImagePreviewBaseProps & {
  title?: string;
  closeButtonText?: string;
  errorMessages?: ImagePreviewErrorMessages;
};
